/**
 * AudioError Component
 *
 * Error display for audio generation failures. ALWAYS renders the
 * user-friendly i18n message — raw backend strings can never leak
 * (see project-error-sanitization-contract).
 */

import { memo } from 'react';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { SanitizedError } from '../../utils/errorSanitizer';

interface AudioErrorProps {
  error: string;
  sanitizedError?: SanitizedError | null;
  onReset: () => void;
}

export const AudioError = memo(function AudioError({
  error: _error,
  sanitizedError,
  onReset,
}: AudioErrorProps) {
  const { t } = useTranslation();

  const displayMessage = t(sanitizedError?.i18nKey ?? 'error.generic.service_unavailable');

  return (
    <div className="card p-6 border-red-200 bg-red-50">
      <div className="flex items-start space-x-3">
        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-medium text-red-800">Generation Failed</h3>
          <p className="text-sm text-red-700 mt-1">{displayMessage}</p>
          {sanitizedError?.errorId && (
            <p className="text-xs text-red-500 opacity-70 mt-1">
              {t('error.generic.support_reference', { id: sanitizedError.errorId })}
            </p>
          )}
          <button
            onClick={onReset}
            className="mt-3 text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
});
